'use client'

import { useCallback, useEffect, useRef } from 'react'
import { debugScreeningProctoringLog } from '@/utils/screening-proctoring-debug.util'

export interface BeaconEvent<T extends string = string> {
    type: T
    timestamp: string
}

export interface UseBeaconEventsConfig<T extends string> {
    /** POST URL for flushing events (e.g. /api/interviews/screeningInterview/proctoring) */
    endpoint: string
    /** Returns auth payload to include in request body. Hook skips flush if null. */
    getAuthPayload: () => Record<string, unknown> | null
    /** Flush interval in ms. Default 15000 */
    flushIntervalMs?: number
    /** Deduplication cooldown in ms. Default 1000 */
    dedupeCooldownMs?: number
    /** When provided, hook attaches blur/pagehide/visibilitychange listeners and calls addEvent with this type */
    blurEventType?: T
}

export function useBeaconEvents<T extends string>(
    config: UseBeaconEventsConfig<T>
): {
    addEvent: (eventType: T) => void
    flushEvents: () => void
} {
    const { endpoint, getAuthPayload, flushIntervalMs = 15 * 1000, dedupeCooldownMs = 1000, blurEventType } = config

    const eventsRef = useRef<BeaconEvent<T>[]>([])
    const timerRef = useRef<NodeJS.Timeout | null>(null)
    const emptyQueueSkipLoggedRef = useRef(false)
    const noAuthSkipLoggedRef = useRef(false)

    const flushEvents = useCallback(() => {
        const authPayload = getAuthPayload()
        if (!authPayload) {
            if (!noAuthSkipLoggedRef.current) {
                noAuthSkipLoggedRef.current = true
                debugScreeningProctoringLog('beacon flush skipped', { endpoint, reason: 'no_auth', queueLength: eventsRef.current.length })
            }
            return
        }
        noAuthSkipLoggedRef.current = false
        if (eventsRef.current.length === 0) {
            if (!emptyQueueSkipLoggedRef.current) {
                emptyQueueSkipLoggedRef.current = true
                debugScreeningProctoringLog('beacon flush skipped', { endpoint, reason: 'empty_queue' })
            }
            return
        }

        const eventsToSend = eventsRef.current.splice(0)
        emptyQueueSkipLoggedRef.current = false
        const payload = JSON.stringify({
            ...authPayload,
            events: eventsToSend,
        })

        if (typeof navigator.sendBeacon !== 'undefined') {
            const ok = navigator.sendBeacon(endpoint, payload)
            debugScreeningProctoringLog('beacon flush sent', {
                endpoint,
                eventCount: eventsToSend.length,
                transport: 'sendBeacon',
                ok,
                types: eventsToSend.map((event) => event.type),
            })
        } else {
            debugScreeningProctoringLog('beacon flush sent', {
                endpoint,
                eventCount: eventsToSend.length,
                transport: 'fetch',
                types: eventsToSend.map((event) => event.type),
            })
            fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload,
            }).catch(() => {
                /* ignore errors */
            })
        }
    }, [endpoint, getAuthPayload])

    const addEvent = useCallback(
        (eventType: T) => {
            const events = eventsRef.current
            const lastEvent = events[events.length - 1]
            const timeNow = Date.now()

            if (!lastEvent || lastEvent.type !== eventType || new Date(lastEvent.timestamp).getTime() + dedupeCooldownMs < timeNow) {
                events.push({ type: eventType, timestamp: new Date().toISOString() })
                emptyQueueSkipLoggedRef.current = false
                debugScreeningProctoringLog('beacon event queued', { type: eventType, queueLength: events.length })
            }
        },
        [dedupeCooldownMs]
    )

    useEffect(() => {
        const onBlur = blurEventType ? () => addEvent(blurEventType) : undefined
        const onPageHide = blurEventType ? () => addEvent(blurEventType) : undefined
        const onVisibilityChange =
            blurEventType && typeof document !== 'undefined'
                ? () => {
                      if (document.visibilityState === 'hidden') {
                          addEvent(blurEventType)
                          flushEvents()
                      }
                  }
                : undefined

        if (onBlur) window.addEventListener('blur', onBlur)
        if (onPageHide) window.addEventListener('pagehide', onPageHide)
        if (onVisibilityChange) document.addEventListener('visibilitychange', onVisibilityChange)
        window.addEventListener('beforeunload', flushEvents)

        timerRef.current = setInterval(flushEvents, flushIntervalMs)

        return () => {
            if (onBlur) window.removeEventListener('blur', onBlur)
            if (onPageHide) window.removeEventListener('pagehide', onPageHide)
            if (onVisibilityChange) document.removeEventListener('visibilitychange', onVisibilityChange)
            window.removeEventListener('beforeunload', flushEvents)

            if (timerRef.current) {
                clearInterval(timerRef.current)
                timerRef.current = null
            }
            flushEvents()
        }
    }, [addEvent, blurEventType, flushEvents, flushIntervalMs])

    return { addEvent, flushEvents }
}
